import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Plus, Search, Users, X } from "lucide-react";
import { useInfiniteContacts } from "@/hooks/useInfiniteContacts";
import { useTags } from "@/hooks/useTags";
import { getDisplayName, getContactAvatarUrl, formatWhatsAppJid } from "@/utils/contactUtils";
import type { Database } from "@/lib/supabase";
import { MobileTagChip } from "../components/MobileTagChip";
import { MobileNewContactSheet } from "../components/MobileNewContactSheet";

type AccountContact = Database["public"]["Tables"]["account_contacts"]["Row"];

const MAX_VISIBLE_TAGS = 3;

const ContactRow = ({
  contact,
  tagColors,
  onSelect,
}: {
  contact: AccountContact;
  tagColors: Record<string, string>;
  onSelect: () => void;
}) => {
  const tags = contact.tags ?? [];
  const visibleTags = tags.slice(0, MAX_VISIBLE_TAGS);
  const hiddenTagCount = tags.length - visibleTags.length;

  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors active:bg-hover"
    >
      <div className="relative shrink-0">
        <img
          src={getContactAvatarUrl(contact)}
          alt=""
          className="h-11 w-11 rounded-full object-cover"
          loading="lazy"
        />
        {contact.is_group && (
          <span className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-background bg-card-muted">
            <Users className="h-2.5 w-2.5 text-muted-foreground" />
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1 border-b border-border-soft pb-3">
        <p className="truncate text-[15px] font-medium text-foreground">
          {getDisplayName(contact)}
        </p>
        <p className="mt-0.5 truncate text-[13px] text-muted-foreground">
          {contact.phone || formatWhatsAppJid(contact.jid)}
        </p>
        {visibleTags.length > 0 && (
          <div className="mt-1.5 flex items-center gap-1 overflow-hidden">
            {visibleTags.map((tag) => (
              <MobileTagChip key={tag} name={tag} color={tagColors[tag]} />
            ))}
            {hiddenTagCount > 0 && (
              <span className="shrink-0 text-[11px] font-medium text-muted-foreground">
                +{hiddenTagCount}
              </span>
            )}
          </div>
        )}
      </div>

      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
    </button>
  );
};

const SkeletonRow = () => (
  <div className="flex animate-pulse items-center gap-3 px-4 py-3">
    <div className="h-11 w-11 shrink-0 rounded-full bg-card-muted" />
    <div className="flex-1 space-y-2">
      <div className="h-3.5 w-2/5 rounded bg-card-muted" />
      <div className="h-3 w-3/5 rounded bg-card-muted" />
    </div>
  </div>
);

const MobileContacts = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [newContactOpen, setNewContactOpen] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // A busca é feita no servidor, então esperamos o usuário parar de digitar
  useEffect(() => {
    const timeout = setTimeout(() => setSearchTerm(searchInput.trim()), 350);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const { contacts, initialLoading, hasMore, loadMore, loadingMore, totalCount } =
    useInfiniteContacts({ searchTerm });
  const { tags } = useTags();

  const tagColors = useMemo(
    () => Object.fromEntries(tags.map((tag) => [tag.name, tag.color])),
    [tags]
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || initialLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, initialLoading, loadMore]);

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <header className="shrink-0 border-b border-border bg-card px-4 pb-3 pt-3">
        <div className="flex items-baseline justify-between gap-2">
          <h1 className="text-xl font-bold text-foreground">Contatos</h1>
          {!initialLoading && (
            <span className="text-[12px] text-muted-foreground">
              {totalCount} {totalCount === 1 ? "contato" : "contatos"}
            </span>
          )}
        </div>
        <div className="relative mt-3">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            inputMode="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Buscar por nome, telefone ou e-mail"
            className="h-10 w-full rounded-full border border-border bg-background pl-9 pr-9 text-[14px] text-foreground outline-none placeholder:text-muted-foreground focus:border-accent-blue"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput("")}
              aria-label="Limpar busca"
              className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground active:bg-hover"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-24">
        {initialLoading ? (
          <div>
            {Array.from({ length: 8 }).map((_, i) => (
              <SkeletonRow key={i} />
            ))}
          </div>
        ) : contacts.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-8 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-card-muted">
              <Users className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-[15px] font-semibold text-foreground">
              {searchTerm ? "Nenhum resultado" : "Nenhum contato"}
            </p>
            <p className="text-[13px] text-muted-foreground">
              {searchTerm
                ? "Tente buscar por outro nome, telefone ou e-mail."
                : "Toque em + para criar o primeiro contato."}
            </p>
          </div>
        ) : (
          <>
            {contacts.map((contact) => (
              <ContactRow
                key={contact.id}
                contact={contact}
                tagColors={tagColors}
                onSelect={() => navigate(`/m/chat/${contact.id}`)}
              />
            ))}
            {hasMore && (
              <div ref={sentinelRef} className="flex items-center justify-center py-4">
                {loadingMore && (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
                )}
              </div>
            )}
          </>
        )}
      </div>

      <button
        type="button"
        onClick={() => setNewContactOpen(true)}
        aria-label="Novo contato"
        className="absolute bottom-5 right-5 flex h-14 w-14 items-center justify-center rounded-full bg-accent-blue text-white shadow-lg shadow-accent-blue/30 transition-transform active:scale-95"
      >
        <Plus className="h-6 w-6" strokeWidth={2.4} />
      </button>

      <MobileNewContactSheet open={newContactOpen} onOpenChange={setNewContactOpen} />
    </div>
  );
};

export default MobileContacts;
